import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, arabicError } from "../lib/api";

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
};

/** Tap mic → record → stop/cancel → transcribed Arabic text is handed back (never auto-sent). */
const VoiceRecorder = ({ onTranscript, disabled, endpoint = "/ai/sourcing/transcribe" }) => {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const cancelledRef = useRef(false);
  const timerRef = useRef(null);

  const cleanup = () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recRef.current = null;
    chunksRef.current = [];
  };
  useEffect(() => cleanup, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("المتصفح لا يدعم تسجيل الصوت"); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      cancelledRef.current = false;
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mime || "audio/webm" });
        const wasCancelled = cancelledRef.current;
        cleanup();
        if (wasCancelled) { setState("idle"); return; }
        if (blob.size < 1000) { setState("idle"); toast.error("التسجيل قصير جداً"); return; }
        setState("transcribing");
        try {
          const fd = new FormData();
          fd.append("audio", blob, `voice.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
          const r = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (r.data.text) onTranscript(r.data.text);
          else toast.error("لم يتم التعرف على كلام واضح، حاول مجدداً");
        } catch (err) { toast.error(arabicError(err)); }
        finally { setState("idle"); }
      };
      rec.start(250);
      recRef.current = rec;
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= 120) recRef.current?.stop();
        return s + 1;
      }), 1000);
      setState("recording");
    } catch (err) {
      cleanup();
      toast.error(err?.name === "NotAllowedError" ? "يرجى السماح باستخدام الميكروفون" : "تعذّر بدء التسجيل");
    }
  };

  const stop = () => { cancelledRef.current = false; recRef.current?.stop(); };
  const cancel = () => { cancelledRef.current = true; recRef.current?.stop(); };

  if (state === "transcribing") {
    return (
      <div className="h-12 px-3 rounded-lg bg-[#EBF4F6] text-[#09637E] text-sm font-bold flex items-center gap-2" data-testid="voice-transcribing">
        <Loader2 size={18} className="animate-spin" /> <span className="hidden sm:inline">جارٍ تحويل الصوت إلى نص...</span>
      </div>
    );
  }
  if (state === "recording") {
    return (
      <div className="h-12 flex items-center gap-2" data-testid="voice-recording-bar">
        <span className="flex items-center gap-2 text-red-600 text-sm font-bold" data-testid="voice-recording-indicator">
          <span className="w-3 h-3 rounded-full bg-red-600 animate-pulse" />
          {String(Math.floor(seconds / 60)).padStart(2, "0")}:{String(seconds % 60).padStart(2, "0")}
        </span>
        <button type="button" onClick={stop} data-testid="voice-stop" title="إيقاف وتحويل إلى نص"
          className="h-12 w-12 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white flex items-center justify-center"><Square size={18} /></button>
        <button type="button" onClick={cancel} data-testid="voice-cancel" title="إلغاء التسجيل"
          className="h-12 w-12 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 flex items-center justify-center"><X size={18} /></button>
      </div>
    );
  }
  return (
    <button type="button" onClick={start} disabled={disabled} data-testid="voice-record"
      title="تسجيل صوتي" aria-label="تسجيل صوتي"
      className="h-12 w-12 rounded-lg border-2 border-[#09637E] text-[#09637E] hover:bg-[#EBF4F6] flex items-center justify-center disabled:opacity-50">
      <Mic size={20} />
    </button>
  );
};

export default VoiceRecorder;
